import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// import DailyInspiration, {
//   marcusAureliusMeditations7_3,
// } from '../../Components/DailyInspiration/DailyInspiration'
import DashboardHeader from '../../Components/DashboardHeader/DashboardHeader'
import FocusSection, {
  DEFAULT_TASK_SORT,
  FocusTask,
  type TaskSortMode,
} from '../../Components/FocusSection/FocusSection'
import HabitsSection from '../../Components/HabitsSection/HabitsSection'
import MoodSection from '../../Components/MoodSection/MoodSection'
import { SkeletonHabitList } from '../../Components/Skeleton/Skeleton'
import SectionHeader from '../../Components/SectionHeader/SectionHeader'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { selectHabitsStatus } from '../../redux/habits'
import {
  createTask,
  deleteTask,
  fetchTasksByDate,
  selectTasksForDate,
  selectTasksStatus,
  toDateKey,
  todayKey,
  updateTask,
} from '../../redux/tasks'
import { formatWeekOfLabel, selectActiveHabits, toWeekKey } from '../../redux/habits'
import {
  celebrateDayComplete,
  celebrateTaskCompleted,
} from '../../feedback/celebrate'
import { sortDashboardTasks } from './sortDashboardTasks'
import styles from './Dashboard.module.css'

const Dashboard = () => {
  const navigate = useNavigate()
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date())
  const [taskSort, setTaskSort] = useState<TaskSortMode>(DEFAULT_TASK_SORT)
  const dispatch = useAppDispatch()

  const selectedKey = toDateKey(selectedDate)
  const tasksForSelectedDate = useAppSelector(selectTasksForDate(selectedKey))
  const tasksStatus = useAppSelector(selectTasksStatus)
  const habitsStatus = useAppSelector(selectHabitsStatus)
  const activeHabits = useAppSelector(selectActiveHabits)
  const habitCount = activeHabits.length
  const habitsWeekStart = toWeekKey(selectedDate)
  const habitsWeekLabel = `Week of ${formatWeekOfLabel(habitsWeekStart)}`

  /**
   * Avoid unmounting `HabitsSection` on every `fetchHabits` pending:
   * with 0 habits, `habitsStatus === 'loading' && habitCount === 0` was true, which
   * replaced the section with a skeleton, unmounted the section, then on success
   * remounted it — which dispatched `fetchHabits` again and caused an infinite loop.
   * Once the first request finishes (success or failure), we keep the section mounted.
   */
  const [habitsListReady, setHabitsListReady] = useState(false)
  useEffect(() => {
    if (habitsStatus === 'succeeded' || habitsStatus === 'failed') {
      setHabitsListReady(true)
    }
  }, [habitsStatus])
  const showHabitsSkeleton =
    !habitsListReady &&
    (habitsStatus === 'idle' ||
      (habitsStatus === 'loading' && habitCount === 0))

  useEffect(() => {
    void dispatch(fetchTasksByDate(selectedKey))
  }, [dispatch, selectedKey])

  const sortedTasksForDay = useMemo(
    () => sortDashboardTasks(tasksForSelectedDate, taskSort),
    [tasksForSelectedDate, taskSort]
  )

  const focusTasks = useMemo<FocusTask[]>(
    () =>
      sortedTasksForDay.map((t) => ({
        id: t.id,
        title: t.title,
        metaLine: t.metaLine,
        completed: !!t.completedAt,
        priority: t.priority,
      })),
    [sortedTasksForDay]
  )

  const showTaskListSkeleton = tasksStatus === 'loading' && focusTasks.length === 0

  const tasksSectionTitle = useMemo(() => {
    if (selectedKey === todayKey()) return 'On your list today'
    const now = new Date()
    const opts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }
    if (selectedDate.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
    const datePart = new Intl.DateTimeFormat('en-US', opts).format(selectedDate)
    return `On your list for ${datePart}`
  }, [selectedKey, selectedDate])

  const onAddTask = useCallback(
    (label: string) => {
      void dispatch(createTask({ title: label, scheduledDate: selectedKey }))
    },
    [dispatch, selectedKey]
  )

  const onToggleTask = useCallback(
    (id: string) => {
      const t = sortedTasksForDay.find((x) => x.id === id)
      if (!t) return
      const willComplete = !t.completedAt
      void dispatch(
        updateTask({
          id,
          changes: willComplete
            ? { completedAt: new Date().toISOString(), status: 'done' }
            : { completedAt: null, status: 'todo' },
        })
      )
      if (!willComplete) return
      const remaining = sortedTasksForDay.filter(
        (x) => x.id !== id && !x.completedAt
      ).length
      if (remaining === 0 && sortedTasksForDay.length > 0) {
        celebrateDayComplete()
      } else {
        celebrateTaskCompleted(t.title)
      }
    },
    [sortedTasksForDay, dispatch]
  )

  const onViewTask = useCallback(
    (id: string) => {
      navigate(`/tasks/${id}`)
    },
    [navigate]
  )

  const onDeleteTask = useCallback(
    (id: string) => {
      const t = sortedTasksForDay.find((x) => x.id === id)
      if (!t) return
      if (!window.confirm(`Delete “${t.title}”? This cannot be undone.`)) return
      void dispatch(deleteTask(id))
    },
    [sortedTasksForDay, dispatch]
  )

  return (
    <div className={styles.container}>
      <DashboardHeader selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      {/* DailyInspiration hero — hidden for now; re-enable import + component above */}
      <FocusSection
        title={tasksSectionTitle}
        tasks={focusTasks}
        tasksScopeKey={selectedKey}
        loading={showTaskListSkeleton}
        taskSort={taskSort}
        onTaskSortChange={setTaskSort}
        onAddTask={onAddTask}
        onToggleTask={onToggleTask}
        onViewTask={onViewTask}
        onDeleteTask={onDeleteTask}
      />
      <section
        className={styles.habitsSection}
        aria-labelledby="dashboard-habits-heading"
      >
        <SectionHeader title="This week's habits" titleId="dashboard-habits-heading">
          <p className={styles.habitsSectionMeta}>
            {habitsWeekLabel}
            {habitCount > 0
              ? ` · ${habitCount === 1 ? '1 habit' : `${habitCount} habits`}`
              : ''}
          </p>
        </SectionHeader>
        {showHabitsSkeleton ? (
          <SkeletonHabitList count={4} />
        ) : (
          <HabitsSection weekStart={habitsWeekStart} />
        )}
      </section>
      <section className={styles.moodSection}>
        <MoodSection dateKey={selectedKey} />
      </section>
    </div>
  )
}

export default Dashboard
